import { mdiClose, mdiDrag, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { defaultSelectStyles } from 'Components/Forms/ReactSelectField.jsx';
import React, { useCallback, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import CreatableSelect from 'react-select/creatable';
import OptionalLabel from './OptionalLabel';

const MultiField = ({
  name,
  values = [],
  push,
  remove,
  move,
  label,
  placeholder = 'Select or create options',
  required = false,
  suggestions = [],
  valueKey = 'value',
  labelKey = 'label',
  isSortable = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('');

  // Convert values to format expected by react-select
  const selectedOptions = useMemo(() => {
    return values.map((item, index) => {
      // For each item, we'll use the valueKey as the display value
      const displayValue = item[valueKey] || '';
      return {
        value: displayValue,
        label: displayValue,
        original: item,
        index,
      };
    });
  }, [values, valueKey]);

  // Convert suggestions to format expected by react-select
  const selectOptions = useMemo(() => {
    // Suggestions can be either strings or objects with the valueKey
    return suggestions
      .filter((suggestion) => {
        const suggestionValue = typeof suggestion === 'object' ? suggestion[valueKey] : suggestion;
        return !values.some((val) => val[valueKey] === suggestionValue);
      })
      .map((suggestion) => {
        const value = typeof suggestion === 'object' ? suggestion[valueKey] : suggestion;
        return {
          value,
          label: value,
          original: suggestion,
        };
      });
  }, [suggestions, values, valueKey]);

  const handleChange = (selected) => {
    // Clear the input
    setInputValue('');

    if (!selected) return;

    // Always create a new object with the valueKey
    const newValue = { [valueKey]: selected.value };
    push(newValue);
  };

  const handleCreate = (inputValue) => {
    if (!inputValue.trim()) return;

    // Create a new object with the valueKey
    const newValue = { [valueKey]: inputValue };
    push(newValue);
    setInputValue('');
  };

  const handleRemove = (index) => {
    remove(index);
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !move) return;

    const fromIndex = result.source.index;
    const toIndex = result.destination.index;

    if (fromIndex === toIndex) return;

    move(fromIndex, toIndex);
  };

  const renderItem = (item, index, provided, snapshot) => {
    // Always use the value from the original object's valueKey
    const displayValue = item.original[valueKey] || '';

    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        className={`relative mb-2 flex items-center justify-between rounded border bg-white p-2 ${snapshot.isDragging ? 'shadow-md' : 'hover:bg-gray-50'}`}
      >
        <div className="flex flex-1 items-center">
          {isSortable && (
            <div
              {...provided.dragHandleProps}
              className="absolute bottom-0 left-0 top-0 mr-2 grid w-8 cursor-move place-items-center text-gray-400 hover:text-gray-600"
            >
              <Icon path={mdiDrag} size={0.8} />
            </div>
          )}
          <span className={`flex-1 ${isSortable ? 'pl-6' : ''}`}>{displayValue}</span>
        </div>
        <button type="button" onClick={() => handleRemove(index)} className="p-1 text-gray-400 hover:text-red-500">
          <Icon path={mdiClose} size={0.8} />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <OptionalLabel required={required} name={name} label={label} />

      {/* Input for adding new items */}
      <div className="mb-4">
        <CreatableSelect
          isClearable
          isSearchable
          inputValue={inputValue}
          onInputChange={setInputValue}
          onChange={handleChange}
          onCreateOption={handleCreate}
          options={selectOptions}
          placeholder={placeholder}
          styles={defaultSelectStyles}
          formatCreateLabel={(inputValue) => (
            <div className="flex items-center">
              <Icon path={mdiPlus} size={0.8} className="mr-1" />
              <span>
                {placeholder} "{inputValue}"
              </span>
            </div>
          )}
          noOptionsMessage={() => 'Type to create a new option'}
          value={null}
        />
      </div>

      {/* List of selected items */}
      {selectedOptions.length > 0 && (
        <div className="mt-2">
          {isSortable ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="selected-items">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {selectedOptions.map((item, index) => (
                      <Draggable key={item.value} draggableId={item.value} index={index}>
                        {(provided, snapshot) => renderItem(item, index, provided, snapshot)}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <div className="space-y-2">
              {selectedOptions.map((item, index) => (
                <div key={item.value} className="flex items-center justify-between rounded border bg-white p-2 hover:bg-gray-50">
                  <span className="flex-1">{item.label}</span>
                  <button type="button" onClick={() => handleRemove(index)} className="p-1 text-gray-400 hover:text-red-500">
                    <Icon path={mdiClose} size={0.8} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiField;
